import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const menuItems = [
    { label: 'Home', path: '/' },
    { label: 'Furniture', path: '/produkter' },
    { label: 'Profile', path: '/profil' },
    { label: 'Instagram', path: 'https://www.instagram.com/heinedesign/', external: true },
    { label: 'Contact', path: '/kontakt' }
  ];

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <header className="sticky top-0 z-50 bg-black/90 backdrop-blur-md border-b border-brand-gray transition-all duration-300">
      <div className="max-w-7xl mx-auto px-6 h-24 flex items-center justify-between">
        {/* Elegant Logo */}
        <Link to="/" className="flex flex-col group py-2 transition-transform duration-300 hover:scale-[1.03] w-fit">
          <span className="text-2xl font-light tracking-[0.2em] text-white uppercase font-display mr-[-0.2em]">
            Heine Design
          </span>
          <div className="h-[1px] w-full bg-white/30 my-1.5" />
          <div 
            className="flex justify-between w-full text-[6.5px] text-brand-charcoal uppercase font-medium font-display whitespace-nowrap"
            aria-label="Danish design and handcraft with pride since 1998."
          >
            {"Danish design and handcraft with pride since 1998.".toUpperCase().split("").map((char, i) => (
              <span key={i} aria-hidden="true" className="select-none">
                {char === " " ? "\u00A0" : char}
              </span>
            ))}
          </div>
        </Link>

        {/* Desktop Navigation (>= 1200px) */}
        <nav className="hidden min-[1200px]:flex items-center space-x-10">
          {menuItems.map((item) => item.external ? (
            <a
              key={item.path}
              href={item.path}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm tracking-[0.2em] uppercase transition-all duration-300 relative py-2 text-brand-charcoal hover:text-white"
            >
              {item.label}
            </a>
          ) : (
            <Link
              key={item.path}
              to={item.path}
              className={`text-sm tracking-[0.2em] uppercase transition-all duration-300 relative py-2 ${
                isActive(item.path)
                  ? 'text-white font-semibold'
                  : 'text-brand-charcoal hover:text-white'
              }`}
            >
              {item.label}
              {isActive(item.path) && (
                <span className="absolute bottom-0 left-0 w-full h-[1.5px] bg-brand-oak" />
              )}
            </Link>
          ))}
        </nav>

        {/* Hamburger Button (< 1200px) */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex min-[1200px]:hidden flex-col justify-between w-6 h-5 z-50 focus:outline-none group relative"
          aria-label="Toggle menu"
        >
          <span
            className={`h-[1.5px] w-full transition-all duration-300 origin-left ${
              isOpen ? 'rotate-45 translate-y-[2px] bg-brand-dark' : 'bg-brand-dark'
            }`}
          />
          <span
            className={`h-[1.5px] w-full transition-all duration-300 ${
              isOpen ? 'opacity-0 bg-brand-dark' : 'opacity-100 bg-brand-dark'
            }`}
          />
          <span
            className={`h-[1.5px] w-full transition-all duration-300 origin-left ${
              isOpen ? '-rotate-45 -translate-y-[2px] bg-brand-dark' : 'bg-brand-dark'
            }`}
          />
        </button>
      </div>

      {/* Mobile Navigation Dropdown */}
      <div
        className={`fixed top-24 left-0 w-full h-[calc(100vh-96px)] bg-black/95 backdrop-blur-md transition-all duration-300 ease-in-out min-[1200px]:hidden overflow-y-auto ${
          isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'
        }`}
      >
        <nav className="flex flex-col items-start justify-center min-h-[70%] space-y-8 px-6 max-w-7xl mx-auto w-full">
          {menuItems.map((item) => item.external ? (
            <a
              key={item.path}
              href={item.path}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setIsOpen(false)}
              className="text-lg tracking-[0.25em] uppercase transition-colors duration-300 py-2 text-white/70 hover:text-white"
            >
              {item.label}
            </a>
          ) : (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setIsOpen(false)}
              className={`text-lg tracking-[0.25em] uppercase transition-colors duration-300 py-2 ${
                isActive(item.path)
                  ? 'text-brand-oak font-semibold border-b border-brand-oak'
                  : 'text-white/70 hover:text-white'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>
      </div>
    </header>
  );
}
